import { create } from 'zustand';
import type { Layer, Selection, ViewerCamera } from '@geosoft/viewer-sdk';
import type { ViewerState as UrlViewerState } from './url/schema';
import type { ScenePreset, SceneSlot } from '@/sdk-stub/gtwin-sdk';
import {
  CLEARED_SCENE_SELECTION,
  clearSelection as buildClearedSceneSelection,
  selectByIndexNodeId as buildSelectionByIndexNodeId,
  selectByLayerId as buildSelectionByLayerId,
  type SceneSelection,
} from '@/app/sceneSelection';
import {
  DEFAULT_BASEMAP_ID,
  loadPreferredBasemapId,
  type BasemapId,
} from '@/engines/maplibre/basemaps';
import type { EnvironmentPresetId } from '@/modules/weather';
import { applyInferredAlternates } from '@/components/layers/alternateGroupInference';
import {
  buildAlternateGroups,
  reconcileAlternateGroupVisibility,
  resolveActiveMemberId,
} from '@/components/layers/alternateGroupModel';
import { emitAlternateSwitch } from '@/components/layers/alternateGroupEvents';
import { isTerrainDerivativeLayer } from '@/components/terrain/terrainDerivedLayers';
import type { RasterRenderMode } from '@/components/raster/rasterSymbology';
import type { VectorStyleState } from '@/components/vector/vectorStyleModel';

export type { RasterRenderMode } from '@/components/raster/rasterSymbology';

export type MapTool =
  | 'select'
  | 'pan'
  | 'measure'
  | 'area';

/**
 * Workspace mode driven by the `#mode=` deep-link fragment per
 * `architecture.md` Â§3.3 + `navigation-map.md` Â§5. Distinct from the
 * underlying `engine` (maplibre vs cesium) â€” the engine still drives the
 * 2D-vs-3D primary renderer, but `mode` is the URL-state concept that
 * later WPs (notably WP-E6) will fully wire to engine swap behaviour.
 */
export type WorkspaceMode = 'map' | '3d' | 'profile' | 'pointcloud' | 'compare';

export type ThreeDCameraMode =
  | 'orbit'
  | 'pan'
  | 'zoom'
  | 'walk'
  | 'fly'
  | 'look-at-selected';

export type ThreeDCanvasTone = 'light' | 'neutral-dark';

export type PointCloudColorMode =
  | 'rgb'
  | 'elevation'
  | 'intensity'
  | 'classification'
  | 'deviation';

export type PointCloudRegistrationState = 'registered' | 'misaligned' | 'needs-review';

/**
 * WP-8 - sensor / live-overlay playback mode (live | replay | simulated).
 * Used by the SensorOverlayToolCard and visualised through the mode chip.
 */
export type SensorPlayMode = 'live' | 'replay' | 'simulated';

/** WP-8 - playback status for live / replay / simulated streams. */
export type SensorPlayStatus = 'playing' | 'paused';

export interface PanoramaBubbleViewState {
  readonly layerId: string | null;
  readonly activeStationId: string | null;
}

export const CLEARED_PANORAMA_BUBBLE_VIEW: PanoramaBubbleViewState = Object.freeze({
  layerId: null,
  activeStationId: null,
});

// ---------------------------------------------------------------------------
// WP-3 â€” Basemap / Raster / Terrain ToolCard state contracts.
//
// These slices back the three ToolCards (Cards 1â€“3 in the masterplan Â§7).
// The fields are intentionally typed at the union/enum level so adapter
// mocks and tests can assert against the exact emitted state without
// reaching into engine internals. The legacy `localStorage` basemap path
// (see `engines/maplibre/basemaps.ts`) stays as a write-through cache so
// the user's last basemap survives a hard reload, but the *source of
// truth* during a session is now this store.
// ---------------------------------------------------------------------------

export type RasterColorRamp =
  | 'arcgis-elevation'
  | 'spectral'
  | 'viridis'
  | 'magma'
  | 'inferno'
  | 'plasma'
  | 'turbo'
  | 'terrain'
  | 'RdBu'
  | 'greyscale';

export type RasterBlendMode = 'normal' | 'multiply' | 'screen' | 'overlay';

export type {
  RasterStretchType,
  RasterClassifyMethod,
  RasterClassBreak,
  RasterUniqueValueRow,
} from '@/components/raster/rasterClassify';

/**
 * Last sampled pixel from the TiTiler `/cog/point` endpoint.
 * Shared by the Raster ToolCard spectral surface and the map-anchored
 * `PixelInspector` popup.
 */
export interface RasterPixelSample {
  readonly lon: number;
  readonly lat: number;
  readonly values: readonly number[];
  readonly bandNames: readonly string[];
  /** "nodata" when every channel returns the configured no-data sentinel. */
  readonly state: 'ok' | 'nodata' | 'error';
  /** When state="error" the underlying `cog/point` HTTP status or message. */
  readonly errorMessage?: string;
}

export interface BasemapToolCardState {
  readonly activeBasemapId: BasemapId;
  /** 0..1; the Card surfaces a 0..100% mono numeric readout. */
  readonly opacity: number;
  readonly visible: boolean;
  readonly labelsOn: boolean;
  readonly brightness: number;
  readonly contrast: number;
}

export interface RasterToolCardState {
  readonly opacity: number;
  /** R/G/B band indices selected via the chip group; -1 → "unset". */
  readonly bands: readonly [number, number, number];
  readonly colorRamp: RasterColorRamp;
  /**
   * ArcGIS Pro–style primary symbology (Stretch / Classify / Discrete /
   * Unique Values / Shaded Relief / …). Legacy QGIS ids still accepted.
   */
  readonly renderMode: RasterRenderMode;
  /**
   * Hillshade / Shaded Relief Z-factor (vertical exaggeration of the DEM
   * before shading).
   */
  readonly hillshadeZFactor: number;
  /** ArcGIS Stretch Type — how the data range maps onto the color scheme. */
  readonly stretchType: import('@/components/raster/rasterClassify').RasterStretchType;
  /** Operator override for stretch low; null → derive from stats / URL. */
  readonly stretchMin: number | null;
  /** Operator override for stretch high; null → derive from stats / URL. */
  readonly stretchMax: number | null;
  /** Classify: number of classes. */
  readonly classCount: number;
  readonly classifyMethod: import('@/components/raster/rasterClassify').RasterClassifyMethod;
  /** Classify: editable class table (color / upper value / label). */
  readonly classBreaks: readonly import('@/components/raster/rasterClassify').RasterClassBreak[];
  /** Discrete: how many colours from the scheme. */
  readonly discreteColorCount: number;
  /** Unique Values: editable value → color table. */
  readonly uniqueValues: readonly import('@/components/raster/rasterClassify').RasterUniqueValueRow[];
  /** Data min used when building interval colormaps (from last stats). */
  readonly symbologyDataMin: number | null;
  /** Data max used when building interval colormaps (from last stats). */
  readonly symbologyDataMax: number | null;
  readonly brightness: number;
  readonly contrast: number;
  readonly saturation: number;
  readonly noDataMask: boolean;
  /**
   * ArcGIS Statistics source for Stretch / Shaded Relief.
   * `dataset` = full-COG stats (default); `dra` = current display extent.
   */
  readonly statisticsSource: import('@/components/raster/rasterDra').RasterStatisticsSource;
  /**
   * ArcGIS Mask → "Display background value" — treat {@link backgroundValue}
   * as transparent (removes DEM/DTM black borders that are real fill pixels,
   * not metadata NoData).
   */
  readonly displayBackgroundValue: boolean;
  /** Pixel value treated as background when {@link displayBackgroundValue} is on. */
  readonly backgroundValue: number;
  /**
   * Slack (0–255) around {@link backgroundValue} when keying the collar out of
   * a tile. Needed because reprojection and overview averaging turn an exact
   * 0 fill into near-0 edge pixels that `nodata=` alone cannot catch.
   */
  readonly backgroundTolerance: number;
  /**
   * ArcGIS Mask → "NoData colour". `transparent` hides the collar (the default);
   * `color` paints it {@link backgroundColor} instead, which is how operators
   * check coverage or match a print background.
   */
  readonly backgroundColorMode: import('@/components/raster/rasterDra').RasterBackgroundColorMode;
  /** Border / no-data colour as `#rrggbb`, used when the mode is `color`. */
  readonly backgroundColor: string;
  readonly blending: RasterBlendMode;
  readonly lastSample: RasterPixelSample | null;
  /**
   * When true, `PixelInspector` samples `/cog/point` on cursor move for the
   * ArcGIS-style Spectral chart. Toggled by the Raster ToolCard so collapsing
   * that section stops hover network work.
   */
  readonly liveSpectralEnabled: boolean;
}

export interface TerrainToolCardState {
  readonly hillshade: boolean;
  readonly contoursOn: boolean;
  readonly contourInterval: number;
  readonly slopeOn: boolean;
  readonly aspectOn: boolean;
  /** 1Ã—â€“20Ã— per Â§7.3; mono numeric readout in the Card. */
  readonly verticalExaggeration: number;
  readonly opacity: number;
  readonly drape: 'none' | 'raster' | 'vector';
  readonly colorRamp: 'hypsometric' | 'plasma' | 'rdylgn' | 'gray' | 'none';
  readonly query: { lon: number; lat: number; elevation: number | null } | null;
}

export interface SavedView {
  id: string;
  name: string;
  visibleLayerIds: string[];
  camera: ViewerCamera | null;
  time: Date | null;
  /** Optional chrome-level selection snapshot restored with the view. */
  sceneSelection?: SceneSelection;
  createdAt: string;
  /**
   * `bookmark` = QGIS-style location bookmark (camera only on restore).
   * `view` / omitted = full saved view (layers + camera + time + selection).
   */
  kind?: 'bookmark' | 'view';
}

// Mock notification state interfaces for WP-E9
export interface MockOfflineState {
  active: boolean;
  extended?: boolean; // > 5 min outage
  since?: string;
  queueDepth?: number;
  retryCount?: number;
}

export interface MockStaleState {
  active: boolean;
  layerName?: string;
  timeAgo?: string;
}

export interface MockRlsState {
  active: boolean;
  layerId?: string;
  policyName?: string;
  correlationId?: string;
}

export interface MockCredentialsState {
  expired: boolean;
  providerName?: string;
  layerName?: string;
  expiredDate?: string;
}

export interface MockQuotaState {
  exceeded: boolean;
  quotaName?: string;
  used?: number;
  total?: number;
  orgName?: string;
  resetTime?: string;
}

export interface MockCostBudgetState {
  used: number;
  total: number;
  window: '24h' | 'session' | 'month';
  resetTime?: string;
}

/**
 * Estate -> viewer launch context (LoR Viewer Bridge). Set by `TwinRoute`
 * when the viewer is opened from the estate admin with `?launch=estate`.
 * `matchedLayerIds` are the catalog layers resolved from the requested focus
 * tokens; `resolved` is false when an association exists but no scene layer
 * could be matched (drives the "pin to scene" banner).
 */
export interface EstateLaunchFocus {
  tokens: string[];
  matchedLayerIds: string[];
  resolved: boolean;
  assetId?: string;
  tenantId?: string;
  label?: string;
}

/**
 * Where a Layer-Properties coordinate override lives. Only `session` is wired
 * today (held in-memory by this store, lost on reload). The field exists so the
 * Coordinates tab can label overrides honestly and so a future `local`
 * (localStorage) or server-persisted tier can be added without a data-model
 * change. See the global Layer Properties console + RFC-112 CRS alignment.
 */
export type CoordinateOverrideScope = 'session' | 'local';

/**
 * Operator-supplied render-frame override for a single layer, keyed by
 * `layer.id` in {@link ViewerState.layerCoordinateOverrides}. Layer-KIND/ID
 * agnostic at the store level — any layer may carry one. Consumed by the
 * diagnostics builder (`LayerDiagnosticsContext.override`) to recompute the
 * effective display/frame CRS without mutating the catalog `Layer`.
 */
export interface LayerCoordinateOverride {
  /** Session-only source CRS assignment; coordinates are reinterpreted, not rewritten. */
  readonly sourceCrs: string | null;
  /** Override display CRS (e.g. `EPSG:28992`), or null to leave unset. */
  readonly displayCrs: string | null;
  /** Optional proj4 definition string for a non-built-in `displayCrs`. */
  readonly displayCrsProj4: string | null;
  /** Temporary backend endpoint containing vector coordinates interpreted in the assigned CRS. */
  readonly vectorSessionFeaturesUrl?: string | null;
  /** Storage tier this override lives in. Always `session` for now. */
  readonly scope: CoordinateOverrideScope;
  /** ISO timestamp of the last edit (UI freshness hint). */
  readonly updatedAt: string;
}

interface ViewerState {
  // ---------------------------------------------------------------------------
  // WP-PP5 â€” gTwin scene preset state (additive; existing API untouched)
  // ---------------------------------------------------------------------------
  /** Id of the currently active scene preset (e.g. "scn-combined"). */
  activePreset: string;
  /** All presets available for the current gTwin (3 defaults + saved). */
  presets: ScenePreset[];
  /** Maps layer.id â†’ SceneSlot so visibility can be filtered per preset. */
  slotMap: Record<string, SceneSlot>;

  setActivePreset: (presetId: string) => void;
  setPresets: (presets: ScenePreset[]) => void;
  setLayerSlot: (layerId: string, slot: SceneSlot) => void;
  resetViewerSession: () => void;

  // ---------------------------------------------------------------------------
  // Existing state
  // ---------------------------------------------------------------------------
  catalog: Layer[];
  visibleLayerIds: Set<string>;
  /**
   * RFC-108-G Phase 2 — active alternate per group. Keys are
   * `alternateGroupKey(member)` (i.e. the primary's layer id); values are
   * the currently-active member's layer id. Populated by `setCatalog`
   * (reconciliation) and updated by `setVisible` when the operator toggles
   * an alternate member. Mounted-set machinery uses this to enforce
   * one-active-per-group at the engine level (hide-3D-Tiles = unmount).
   */
  activeAlternateByGroup: Map<string, string>;
  selection: Selection | null;
  /**
   * WP-2 â€” stable scene-selection contract for the right-rail
   * `ToolCardDock` and the `SemanticPropertyCard`. See
   * `app/sceneSelection.ts` for the contract; the legacy `selection`
   * field above remains for engine pickers.
   */
  sceneSelection: SceneSelection;
  /** LoR7 — active panorama-bubble layer + station for in-canvas bubble view. */
  panoramaBubbleView: PanoramaBubbleViewState;
  camera: ViewerCamera | null;
  /** Estate -> viewer launch focus (LoR Viewer Bridge); null in normal opens. */
  launchFocus: EstateLaunchFocus | null;
  time: Date | null;
  mapTool: MapTool;
  threeDCameraMode: ThreeDCameraMode;
  threeDCanvasTone: ThreeDCanvasTone;
  threeDExplodeFactor: number;
  pointCloudColorMode: PointCloudColorMode;
  pointCloudSliceOffset: number;
  pointCloudDensityTarget: number;
  pointCloudRegistrationState: PointCloudRegistrationState;
  /** WP-5: UI mirror of point-cloud point size in pixels (0 = auto). */
  pointCloudPointSize: number;
  /** WP-5: UI mirror of active ASPRS classification filter (empty = show all). */
  pointCloudClassificationFilter: ReadonlySet<number>;
  /** WP-8 - sensor / live-overlay playback mode. */
  sensorPlayMode: SensorPlayMode;
  /** WP-8 - playback status. */
  sensorPlayStatus: SensorPlayStatus;
  permissionDeniedMessage: string | null;

  // WP-3 â€” Basemap / Raster / Terrain ToolCards (Cards 1â€“3, masterplan Â§7).
  basemapToolCard: BasemapToolCardState;
  rasterToolCard: RasterToolCardState;
  terrainToolCard: TerrainToolCardState;
  /** Session-scoped vector styles keyed by layer id. */
  vectorStyleOverrides: Readonly<Record<string, VectorStyleState>>;

  savedViews: SavedView[];
  /** Baseline scenario for 4D compare (RFC-102) â€” id matches a `SavedView.id` when set. */
  activeScenarioId: string | null;
  /** Second scenario in compare; must differ from `activeScenarioId` when set. */
  compareWithScenarioId: string | null;
  /**
   * Active workspace mode driven by `#mode=` per architecture.md Â§3.3.
   * Independent of `engine` (maplibre vs cesium); WP-E6 will reconcile
   * the two by mapping mode â†’ engine selection.
   */
  mode: WorkspaceMode;
  /**
   * Read-only flag set when the URL was loaded via `#viewro=`. Drives
   * the recipient banner (GVW-108 / GVW-143) and disables editing in
   * downstream surfaces wired in later WPs.
   */
  readOnly: boolean;

  // Mock notification states for WP-E9 testing
  mockOfflineState: MockOfflineState | null;
  mockStaleState: MockStaleState | null; 
  mockRlsState: MockRlsState | null;
  mockCredentialsState: MockCredentialsState | null;
  mockQuotaState: MockQuotaState | null;
  mockCostBudgetState: MockCostBudgetState | null;

  setCatalog: (layers: Layer[]) => void;
  /**
   * Replace one catalog layer in place without resetting visibility.
   * Used by georeference (and similar) so a style/bbox patch does not
   * remount the whole scene tree.
   */
  patchCatalogLayer: (id: string, updater: (layer: Layer) => Layer) => void;
  toggleLayer: (id: string) => void;
  setVisible: (id: string, visible: boolean) => void;
  setManyVisible: (ids: ReadonlyArray<string>, visible: boolean) => void;
  soloLayer: (id: string) => void;
  removeLayer: (id: string) => void;
  setSelection: (s: Selection | null) => void;
  /** WP-2 â€” set the right-rail scene selection (any kind). */
  setSceneSelection: (selection: SceneSelection) => void;
  /** WP-2 â€” convenience: select a renderable layer by id. */
  selectByLayerId: (
    layerId: string,
    options?: Parameters<typeof buildSelectionByLayerId>[1],
  ) => SceneSelection;
  /** WP-2 â€” convenience: select a scene-index node by id. */
  selectByIndexNodeId: (
    indexNodeId: string,
    options?: Parameters<typeof buildSelectionByIndexNodeId>[1],
  ) => SceneSelection;
  /** WP-2 â€” clear the scene selection (idempotent). */
  clearSceneSelection: (
    source?: SceneSelection['source'],
  ) => SceneSelection;
  /** LoR7 — open or update the in-canvas panorama bubble view. */
  setPanoramaBubbleView: (patch: Partial<PanoramaBubbleViewState>) => void;
  setCamera: (c: ViewerCamera) => void;
  setLaunchFocus: (focus: EstateLaunchFocus | null) => void;
  setTime: (t: Date | null) => void;
  setMapTool: (tool: MapTool) => void;
  setThreeDCameraMode: (mode: ThreeDCameraMode) => void;
  setThreeDCanvasTone: (tone: ThreeDCanvasTone) => void;
  setThreeDExplodeFactor: (value: number) => void;
  setPointCloudColorMode: (mode: PointCloudColorMode) => void;
  setPointCloudSliceOffset: (value: number) => void;
  setPointCloudDensityTarget: (value: number) => void;
  setPointCloudRegistrationState: (state: PointCloudRegistrationState) => void;
  /** WP-5: update the UI mirror of the active point size in pixels. */
  setPointCloudPointSize: (value: number) => void;
  /** WP-5: replace the active ASPRS classification filter. */
  setPointCloudClassificationFilter: (classes: ReadonlySet<number>) => void;
  /** WP-8 - set the sensor playback mode. */
  setSensorPlayMode: (mode: SensorPlayMode) => void;
  /** WP-8 - set the sensor playback status. */
  setSensorPlayStatus: (status: SensorPlayStatus) => void;
  /** WP-8 - toggle play / pause; returns the next status. */
  toggleSensorPlayback: () => SensorPlayStatus;
  setPermissionDeniedMessage: (message: string | null) => void;
  clearPermissionDeniedMessage: () => void;

  // WP-3 ToolCard setters (Cards 1â€“3).
  setBasemapToolCard: (patch: Partial<BasemapToolCardState>) => void;
  setRasterToolCard: (patch: Partial<RasterToolCardState>) => void;
  setTerrainToolCard: (patch: Partial<TerrainToolCardState>) => void;
  setVectorStyleOverride: (layerId: string, style: VectorStyleState) => void;
  saveCurrentView: (name?: string) => void;
  /** Save the current camera as a QGIS-style location bookmark (camera-only restore). */
  saveBookmarkLocation: (name?: string) => void;
  restoreSavedView: (id: string) => void;
  /** Restore only the camera pose for a bookmark (does not change layers/time). */
  restoreBookmarkLocation: (id: string) => void;
  removeSavedView: (id: string) => void;
  setCompareScenarios: (activeId: string | null, compareWithId: string | null) => void;
  clearCompare: () => void;
  setMode: (mode: WorkspaceMode) => void;
  setReadOnly: (value: boolean) => void;
  /**
   * Hydrate the store from a decoded URL-state payload (per
   * `architecture.md` Â§3.3 deep-link table for `#view=` / `#viewro=`).
   *
   * Sets `visibleLayerIds`, `camera`, `time`, `selection` (cleared,
   * since URL state does not carry it) and propagates `mode` when the
   * payload's `primaryEngine` maps unambiguously. Catalog itself is NOT
   * replaced â€” the catalog is the orgs/STAC inventory, not part of the
   * shareable URL state.
   */
  hydrateFromUrlState: (payload: UrlViewerState, opts?: { readOnly?: boolean }) => void;
  
  // Mock notification state setters for WP-E9 testing
  setMockOfflineState: (state: MockOfflineState | null) => void;
  setMockStaleState: (state: MockStaleState | null) => void;
  setMockRlsState: (state: MockRlsState | null) => void;
  setMockCredentialsState: (state: MockCredentialsState | null) => void;
  setMockQuotaState: (state: MockQuotaState | null) => void;
  setMockCostBudgetState: (state: MockCostBudgetState | null) => void;
  
  // Test fixture helpers for WP-E9
  initTestNotificationStates: () => void;
  triggerOfflineState: () => void;
  triggerStaleState: () => void;
  triggerRlsState: () => void;
  triggerCredentialsExpired: () => void;
  triggerQuotaExceeded: () => void;
  triggerCostBudgetWarning: () => void;
  triggerCostBudgetReached: () => void;

  // ---------------------------------------------------------------------------
  // INIT-2026-Q3-WEATHER-ENVIRONMENT — environment layer state
  // ---------------------------------------------------------------------------
  environmentPreset: EnvironmentPresetId;
  environmentMasterEnabled: boolean;
  environmentParameters: Record<string, Record<string, unknown>>;

  setEnvironmentPreset: (preset: EnvironmentPresetId) => void;
  setEnvironmentMasterEnabled: (enabled: boolean) => void;
  setEnvironmentParameter: (layerId: string, key: string, value: unknown) => void;

  // ---------------------------------------------------------------------------
  // Global Layer Properties — session coordinate overrides (labeled, not persisted)
  // ---------------------------------------------------------------------------
  /**
   * Per-layer render-frame overrides keyed by `layer.id`. Session-scoped:
   * held in this store only, never written to disk or the gTwin until a
   * persistence tier is implemented. Empty by default.
   */
  layerCoordinateOverrides: Record<string, LayerCoordinateOverride>;
  /**
   * Set (or merge) a session coordinate override for `layerId`. A patch of
   * `{ displayCrs: null, displayCrsProj4: null }` clears both fields but keeps
   * the entry; use {@link clearLayerCoordinateOverride} to remove it entirely.
   */
  setLayerCoordinateOverride: (
    layerId: string,
    patch: { sourceCrs?: string | null; displayCrs?: string | null; displayCrsProj4?: string | null; vectorSessionFeaturesUrl?: string | null },
  ) => void;
  /** Remove the session override for `layerId` (revert to converter metadata). */
  clearLayerCoordinateOverride: (layerId: string) => void;
}

/** Trim a coordinate-override field; treat empty/whitespace as "unset" (null). */
function normalizeOverrideValue(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Idle Raster ToolCard state.
 *
 * Shared by the store's initial state and {@link ViewerState.resetViewerSession}
 * so opening a second project cannot inherit the first project's symbology.
 */
export const DEFAULT_RASTER_TOOL_CARD: RasterToolCardState = {
  opacity: 1,
  bands: [1, 2, 3],
  colorRamp: 'viridis',
  renderMode: 'multiband-color',
  hillshadeZFactor: 1,
  stretchType: 'percent-clip',
  stretchMin: null,
  stretchMax: null,
  classCount: 5,
  classifyMethod: 'equal-interval',
  classBreaks: [],
  discreteColorCount: 10,
  uniqueValues: [],
  symbologyDataMin: null,
  symbologyDataMax: null,
  brightness: 1,
  contrast: 1,
  saturation: 1,
  noDataMask: true,
  statisticsSource: 'dataset',
  displayBackgroundValue: false,
  backgroundValue: 0,
  backgroundTolerance: 8,
  backgroundColorMode: 'transparent',
  backgroundColor: '#ffffff',
  blending: 'normal',
  lastSample: null,
  liveSpectralEnabled: false,
};

/** Idle Terrain ToolCard state. Reset alongside the raster card. */
export const DEFAULT_TERRAIN_TOOL_CARD: TerrainToolCardState = {
  hillshade: false,
  contoursOn: false,
  contourInterval: 10,
  slopeOn: false,
  aspectOn: false,
  verticalExaggeration: 1,
  opacity: 1,
  drape: 'none',
  colorRamp: 'hypsometric',
  query: null,
};

export const useViewerStore = create<ViewerState>((set, get) => ({
  // WP-PP5 preset state (additive)
  activePreset: 'scn-combined',
  presets: [],
  slotMap: {},

  setActivePreset: (activePreset) => set({ activePreset }),
  setPresets: (presets) => set({ presets }),
  setLayerSlot: (layerId, slot) =>
    set((s) => ({ slotMap: { ...s.slotMap, [layerId]: slot } })),
  resetViewerSession: () =>
    set({
      activePreset: 'scn-combined',
      presets: [],
      slotMap: {},
      catalog: [],
      visibleLayerIds: new Set(),
      activeAlternateByGroup: new Map(),
      selection: null,
      sceneSelection: CLEARED_SCENE_SELECTION,
      panoramaBubbleView: CLEARED_PANORAMA_BUBBLE_VIEW,
      camera: null,
      launchFocus: null,
      permissionDeniedMessage: null,
      layerCoordinateOverrides: {},
      // Layer-scoped card state. Without these a second project opens with the
      // first project's symbology already applied to whatever it selects.
      rasterToolCard: DEFAULT_RASTER_TOOL_CARD,
      terrainToolCard: DEFAULT_TERRAIN_TOOL_CARD,
      vectorStyleOverrides: {},
      // Saved views are per-project and are only re-hydrated when the incoming
      // project actually has some, so a stale list would otherwise survive.
      savedViews: [],
    }),

  // Existing state
  catalog: [],
  visibleLayerIds: new Set(),
  activeAlternateByGroup: new Map(),
  selection: null,
  sceneSelection: CLEARED_SCENE_SELECTION,
  panoramaBubbleView: CLEARED_PANORAMA_BUBBLE_VIEW,
  camera: null,
  launchFocus: null,
  time: null,
  mapTool: 'select',
  threeDCameraMode: 'orbit',
  threeDCanvasTone: 'light',
  threeDExplodeFactor: 0,
  pointCloudColorMode: 'rgb',
  pointCloudSliceOffset: 0,
  pointCloudDensityTarget: 0.75,
  pointCloudRegistrationState: 'registered',
  pointCloudPointSize: 0,
  pointCloudClassificationFilter: new Set<number>(),
  sensorPlayMode: 'live',
  sensorPlayStatus: 'playing',
  permissionDeniedMessage: null,
  basemapToolCard: {
    activeBasemapId:
      typeof window === 'undefined'
        ? DEFAULT_BASEMAP_ID
        : loadPreferredBasemapId(),
    opacity: 1,
    visible: true,
    labelsOn: true,
    brightness: 1,
    contrast: 1,
  },
  rasterToolCard: DEFAULT_RASTER_TOOL_CARD,
  terrainToolCard: DEFAULT_TERRAIN_TOOL_CARD,
  vectorStyleOverrides: {},
  savedViews: [],
  activeScenarioId: null,
  compareWithScenarioId: null,
  mode: 'map',
  readOnly: false,

  // Initialize mock notification states as null (backend not deployed)
  mockOfflineState: null,
  mockStaleState: null,
  mockRlsState: null,
  mockCredentialsState: null,
  mockQuotaState: null,
  mockCostBudgetState: null,

  setCatalog: (layers) => {
    // Phase 2 / RFC-108-G — apply viewer-side alternate-group inference
    // before storing the catalog. Layers already carrying `alternateOf*`
    // fields from the envelope or converter are left untouched; only
    // unannotated multi-LoD 3D Tiles get the heuristic.
    const annotated = applyInferredAlternates(layers);
    // ADR-017 / `LAYER_COMPOSITION_AND_ACTIVATION.md` §2.3 — `setCatalog`
    // no longer marks EVERY layer visible. Layers with `activationTier ===
    // 'on-demand'` are registered (in catalog + scene tree) but stay hidden
    // until the user toggles them. Layers without an explicit tier default
    // to visible to preserve compatibility with intake paths that don't
    // route through the policy (legacy IntakeRoute, ad-hoc tests).
    const initialVisibilityEntries = annotated.map((l) => ({
      layerId: l.id,
      // Terrain derivatives (hillshade/slope/aspect/contours) default OFF —
      // stacking them on the DEM paints TV-static until the user toggles one.
      visible:
        l.activationTier !== 'on-demand'
        && !isTerrainDerivativeLayer(l),
    }));
    // Phase 2 / RFC-108-G — reconcile alternate groups so only the
    // default-active member of each multi-member group is visible
    // (and therefore mountable). The active id is computed from
    // persisted visibility + preference; the rest of the group becomes
    // `visible: false` so the engine's hide-on-3D-Tiles-unmount path
    // keeps memory clean.
    const reconciled = reconcileAlternateGroupVisibility(
      annotated,
      initialVisibilityEntries,
    );
    set({
      catalog: annotated,
      visibleLayerIds: new Set(
        reconciled.visibilityEntries.filter((e) => e.visible).map((e) => e.layerId),
      ),
      activeAlternateByGroup: reconciled.activeByGroup,
      permissionDeniedMessage: null,
    });
  },

  patchCatalogLayer: (id, updater) =>
    set((s) => {
      let changed = false;
      const catalog = s.catalog.map((layer) => {
        if (layer.id !== id) return layer;
        changed = true;
        return updater(layer);
      });
      return changed ? { catalog } : s;
    }),

  toggleLayer: (id) =>
    set((s) => {
      const next = new Set(s.visibleLayerIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { visibleLayerIds: next };
    }),

  setVisible: (id, visible) =>
    set((s) => {
      const next = new Set(s.visibleLayerIds);
      // Phase 2 / RFC-108-G — if `id` belongs to an alternate group with
      // multiple members, toggle the group's active member rather than the
      // single layer: making any member visible hides every sibling and
      // emits `viewer:alternate-switch` so the engine can crossfade and
      // unmount the outgoing tileset. Toggling OFF leaves the group's
      // `active` pointer untouched (so the next ON-toggle returns to the
      // last-active variant).
      const targetLayer = s.catalog.find((l) => l.id === id);
      if (!targetLayer) {
        if (visible) next.add(id);
        else next.delete(id);
        return { visibleLayerIds: next };
      }
      const groups = buildAlternateGroups(s.catalog);
      const group = groups.find((g) =>
        g.members.some((m) => m.layer.id === id),
      );
      if (!group || group.members.length <= 1) {
        if (visible) next.add(id);
        else next.delete(id);
        return { visibleLayerIds: next };
      }
      const previousActiveId = s.activeAlternateByGroup.get(group.groupKey)
        ?? resolveActiveMemberId(group, s.visibleLayerIds, s.activeAlternateByGroup);
      const nextActiveByGroup = new Map(s.activeAlternateByGroup);
      if (visible) {
        // Make `id` the active alternate; hide every sibling.
        for (const member of group.members) {
          if (member.layer.id === id) next.add(member.layer.id);
          else next.delete(member.layer.id);
        }
        nextActiveByGroup.set(group.groupKey, id);
        if (previousActiveId !== id) {
          emitAlternateSwitch({
            groupKey: group.groupKey,
            fromLayerId: previousActiveId ?? null,
            toLayerId: id,
            groupBecomingVisible: !s.visibleLayerIds.has(previousActiveId ?? ''),
          });
        }
      } else {
        // Hiding any member hides the whole group; active pointer is
        // preserved so the next ON-toggle restores the last variant.
        for (const member of group.members) next.delete(member.layer.id);
      }
      return { visibleLayerIds: next, activeAlternateByGroup: nextActiveByGroup };
    }),

  setManyVisible: (ids, visible) =>
    set((s) => {
      if (ids.length === 0) return s;
      const next = new Set(s.visibleLayerIds);
      for (const id of ids) {
        if (visible) next.add(id);
        else next.delete(id);
      }
      return { visibleLayerIds: next };
    }),

  soloLayer: (id) =>
    set((s) => {
      const known = s.catalog.some((layer) => layer.id === id);
      if (!known) return s;
      return { visibleLayerIds: new Set([id]) };
    }),

  removeLayer: (id) =>
    set((s) => {
      const nextVisible = new Set(s.visibleLayerIds);
      nextVisible.delete(id);
      const { [id]: _removedSlot, ...slotMap } = s.slotMap;
      return {
        catalog: s.catalog.filter((layer) => layer.id !== id),
        visibleLayerIds: nextVisible,
        slotMap,
      };
    }),

  setSelection: (selection) => set({ selection }),
  setSceneSelection: (sceneSelection) => set({ sceneSelection }),
  selectByLayerId: (layerId, options) => {
    const next = buildSelectionByLayerId(layerId, options);
    set({ sceneSelection: next });
    return next;
  },
  selectByIndexNodeId: (indexNodeId, options) => {
    const next = buildSelectionByIndexNodeId(indexNodeId, options);
    set({ sceneSelection: next });
    return next;
  },
  clearSceneSelection: (source) => {
    const next = buildClearedSceneSelection(source);
    set({ sceneSelection: next });
    return next;
  },
  setPanoramaBubbleView: (patch) =>
    set((state) => ({
      panoramaBubbleView: {
        ...state.panoramaBubbleView,
        ...patch,
      },
    })),
  setCamera: (camera) => set({ camera }),
  setLaunchFocus: (launchFocus) => set({ launchFocus }),
  setTime: (time) => set({ time }),
  setMapTool: (mapTool) => set({ mapTool }),
  setThreeDCameraMode: (threeDCameraMode) => set({ threeDCameraMode }),
  setThreeDCanvasTone: (threeDCanvasTone) => set({ threeDCanvasTone }),
  setThreeDExplodeFactor: (threeDExplodeFactor) =>
    set({ threeDExplodeFactor: Math.max(0, Math.min(1, threeDExplodeFactor)) }),
  setPointCloudColorMode: (pointCloudColorMode) => set({ pointCloudColorMode }),
  setPointCloudSliceOffset: (pointCloudSliceOffset) =>
    set({ pointCloudSliceOffset: Math.max(-1, Math.min(1, pointCloudSliceOffset)) }),
  setPointCloudDensityTarget: (pointCloudDensityTarget) =>
    set({ pointCloudDensityTarget: Math.max(0.1, Math.min(1, pointCloudDensityTarget)) }),
  setPointCloudRegistrationState: (pointCloudRegistrationState) => set({ pointCloudRegistrationState }),
  setPointCloudPointSize: (pointCloudPointSize) =>
    set({ pointCloudPointSize: Math.max(0, Math.min(8, pointCloudPointSize)) }),
  setPointCloudClassificationFilter: (classes) =>
    set({ pointCloudClassificationFilter: new Set(classes) }),
  setSensorPlayMode: (sensorPlayMode) => set({ sensorPlayMode }),
  setSensorPlayStatus: (sensorPlayStatus) => set({ sensorPlayStatus }),
  toggleSensorPlayback: (): SensorPlayStatus => {
    const next: SensorPlayStatus =
      get().sensorPlayStatus === 'playing' ? 'paused' : 'playing';
    set({ sensorPlayStatus: next });
    return next;
  },
  setPermissionDeniedMessage: (permissionDeniedMessage) => set({ permissionDeniedMessage }),
  clearPermissionDeniedMessage: () => set({ permissionDeniedMessage: null }),

  // WP-3 ToolCard setters â€” partial patches keep test assertions narrow.
  setBasemapToolCard: (patch) =>
    set((state) => ({ basemapToolCard: { ...state.basemapToolCard, ...patch } })),
  setRasterToolCard: (patch) =>
    set((state) => ({ rasterToolCard: { ...state.rasterToolCard, ...patch } })),
  setTerrainToolCard: (patch) =>
    set((state) => ({ terrainToolCard: { ...state.terrainToolCard, ...patch } })),
  setVectorStyleOverride: (layerId, style) =>
    set((state) => ({
      vectorStyleOverrides: {
        ...state.vectorStyleOverrides,
        [layerId]: style,
      },
    })),
  saveCurrentView: (name) =>
    set((state) => {
      const id = `view-${Date.now()}`;
      const index = state.savedViews.length + 1;
      const viewName = name?.trim() || `Saved view ${index}`;
      const next: SavedView = {
        id,
        name: viewName,
        kind: 'view',
        visibleLayerIds: [...state.visibleLayerIds],
        camera: state.camera,
        time: state.time,
        sceneSelection:
          state.sceneSelection.kind === 'cleared'
            ? undefined
            : state.sceneSelection,
        createdAt: new Date().toISOString(),
      };
      return { savedViews: [next, ...state.savedViews].slice(0, 12) };
    }),
  saveBookmarkLocation: (name) =>
    set((state) => {
      if (!state.camera) return state;
      const bookmarks = state.savedViews.filter((v) => v.kind === 'bookmark');
      const id = `bookmark-${Date.now()}`;
      const index = bookmarks.length + 1;
      const bookmarkName = name?.trim() || `Bookmark ${index}`;
      const next: SavedView = {
        id,
        name: bookmarkName,
        kind: 'bookmark',
        visibleLayerIds: [...state.visibleLayerIds],
        camera: state.camera,
        time: state.time,
        createdAt: new Date().toISOString(),
      };
      return { savedViews: [next, ...state.savedViews].slice(0, 12) };
    }),
  restoreSavedView: (id) =>
    set((state) => {
      const view = state.savedViews.find((candidate) => candidate.id === id);
      if (!view) return state;
      if (view.kind === 'bookmark') {
        return { camera: view.camera };
      }
      return {
        visibleLayerIds: new Set(view.visibleLayerIds),
        camera: view.camera,
        time: view.time,
        sceneSelection: view.sceneSelection ?? CLEARED_SCENE_SELECTION,
      };
    }),
  restoreBookmarkLocation: (id) =>
    set((state) => {
      const view = state.savedViews.find((candidate) => candidate.id === id);
      if (!view?.camera) return state;
      return { camera: view.camera };
    }),
  removeSavedView: (id) =>
    set((state) => ({
      savedViews: state.savedViews.filter((view) => view.id !== id),
    })),

  setCompareScenarios: (activeId, compareWithId) => set({ activeScenarioId: activeId, compareWithScenarioId: compareWithId }),

  clearCompare: () => set({ activeScenarioId: null, compareWithScenarioId: null }),

  setMode: (mode) => set({ mode }),
  setReadOnly: (readOnly) => set({ readOnly }),

  // Mock notification state setters for WP-E9 testing
  setMockOfflineState: (mockOfflineState) => set({ mockOfflineState }),
  setMockStaleState: (mockStaleState) => set({ mockStaleState }),
  setMockRlsState: (mockRlsState) => set({ mockRlsState }),
  setMockCredentialsState: (mockCredentialsState) => set({ mockCredentialsState }),
  setMockQuotaState: (mockQuotaState) => set({ mockQuotaState }),
  setMockCostBudgetState: (mockCostBudgetState) => set({ mockCostBudgetState }),

  // Test fixture helpers for WP-E9
  initTestNotificationStates: () => {
    // Initialize default mock states for testing
    set({
      mockOfflineState: null,
      mockStaleState: null,
      mockRlsState: null,
      mockCredentialsState: null,
      mockQuotaState: null,
      mockCostBudgetState: {
        used: 45,
        total: 100,
        window: 'session',
        resetTime: '6:00 PM'
      }
    });
  },

  triggerOfflineState: () => {
    set({
      mockOfflineState: { 
        active: true, 
        extended: false, 
        since: new Date().toISOString(),
        queueDepth: 3,
        retryCount: 0
      }
    });
  },

  triggerStaleState: () => {
    set({
      mockStaleState: {
        active: true,
        layerName: 'Bathymetry 2026',
        timeAgo: '2 hours ago'
      }
    });
  },

  triggerRlsState: () => {
    set({
      mockRlsState: {
        active: true,
        layerId: 'protected-layer-001',
        policyName: 'DEFENCE_RESTRICTED',
        correlationId: 'rls-' + Date.now()
      }
    });
  },

  triggerCredentialsExpired: () => {
    set({
      mockCredentialsState: {
        expired: true,
        providerName: 'Mapbox',
        layerName: 'Satellite Imagery',
        expiredDate: '2026-04-28'
      }
    });
  },

  triggerQuotaExceeded: () => {
    set({
      mockQuotaState: {
        exceeded: true,
        quotaName: 'tiles',
        used: 10000,
        total: 10000,
        orgName: 'Acme Corporation',
        resetTime: '12:00 AM UTC'
      }
    });
  },

  triggerCostBudgetWarning: () => {
    set({
      mockCostBudgetState: {
        used: 85,
        total: 100,
        window: 'session',
        resetTime: '6:00 PM'
      }
    });
  },

  triggerCostBudgetReached: () => {
    set({
      mockCostBudgetState: {
        used: 100,
        total: 100,
        window: '24h',
        resetTime: '12:00 AM UTC'
      }
    });
  },

  // INIT-2026-Q3-WEATHER-ENVIRONMENT — environment layer state
  environmentPreset: 'clear',
  environmentMasterEnabled: true,
  environmentParameters: {},

  setEnvironmentPreset: (environmentPreset) => set({ environmentPreset }),
  setEnvironmentMasterEnabled: (environmentMasterEnabled) =>
    set({ environmentMasterEnabled }),
  setEnvironmentParameter: (layerId, key, value) =>
    set((s) => ({
      environmentParameters: {
        ...s.environmentParameters,
        [layerId]: {
          ...(s.environmentParameters[layerId] ?? {}),
          [key]: value,
        },
      },
    })),

  // Global Layer Properties — session coordinate overrides.
  layerCoordinateOverrides: {},
  setLayerCoordinateOverride: (layerId, patch) =>
    set((s) => {
      const previous = s.layerCoordinateOverrides[layerId];
      const next: LayerCoordinateOverride = {
        sourceCrs:
          'sourceCrs' in patch ? normalizeOverrideValue(patch.sourceCrs) : previous?.sourceCrs ?? null,
        displayCrs:
          'displayCrs' in patch ? normalizeOverrideValue(patch.displayCrs) : previous?.displayCrs ?? null,
        displayCrsProj4:
          'displayCrsProj4' in patch
            ? normalizeOverrideValue(patch.displayCrsProj4)
            : previous?.displayCrsProj4 ?? null,
        vectorSessionFeaturesUrl:
          'vectorSessionFeaturesUrl' in patch
            ? normalizeOverrideValue(patch.vectorSessionFeaturesUrl)
            : previous?.vectorSessionFeaturesUrl ?? null,
        scope: 'session',
        updatedAt: new Date().toISOString(),
      };
      return {
        layerCoordinateOverrides: { ...s.layerCoordinateOverrides, [layerId]: next },
      };
    }),
  clearLayerCoordinateOverride: (layerId) =>
    set((s) => {
      if (!(layerId in s.layerCoordinateOverrides)) return s;
      const { [layerId]: _removed, ...rest } = s.layerCoordinateOverrides;
      return { layerCoordinateOverrides: rest };
    }),

  hydrateFromUrlState: (payload, opts) =>
    set((state) => {
      const next: Partial<ViewerState> = {};
      if (Array.isArray(payload.visibleLayerIds)) {
        next.visibleLayerIds = new Set(payload.visibleLayerIds);
      }
      if (payload.view?.center) {
        const camera: ViewerCamera = {
          lonLat: [payload.view.center[0], payload.view.center[1]],
          zoom: payload.view.zoom,
        };
        if (payload.view.heading != null || payload.view.pitch != null) {
          camera.hpr = [payload.view.heading ?? 0, payload.view.pitch ?? 0, 0];
        }
        next.camera = camera;
      }
      if (payload.time !== undefined) {
        next.time = payload.time;
      }
      next.selection = null;
      if (opts?.readOnly !== undefined) {
        next.readOnly = opts.readOnly;
      }
      return { ...state, ...next };
    }),
}));

// ---------------------------------------------------------------------------
// WP-PP5 selectors
// ---------------------------------------------------------------------------

/**
 * Returns the layers whose SceneSlot is in the active preset's `show` array.
 * Layers without a slot entry are treated as always-visible (backward-compat).
 */
export function selectVisibleLayers(state: ReturnType<typeof useViewerStore.getState>): Layer[] {
  const activePresetObj = state.presets.find((p) => p.id === state.activePreset);
  if (!activePresetObj) return state.catalog;
  return state.catalog.filter((layer) => {
    const slot = state.slotMap[layer.id];
    if (!slot) return true; // no slot info â†’ always visible
    return activePresetObj.show.includes(slot);
  });
}
